// `spaces` PATCH dispatch for updateSpaceAlias (FFR2 — field edit of an
// existing room's alias during capture).
//
// STANDING INVARIANT (relied on by connector.ts's spaces-PATCH branch):
// the ONLY local UPDATE ever issued against the spaces table is `alias`
// (capture-client-powersync-update-space-alias.ts). So a spaces-PATCH
// CRUD entry ⟺ an alias edit, and this handler can map it to the
// dedicated alias endpoint without inspecting which column changed. If a
// future code path ever does `UPDATE spaces SET <non-alias column>`
// locally it would be MIS-ROUTED here and silently dropped — never do
// that.
//
// THE WEDGE MITIGATION (read closely):
//   A PowerSync column-only PATCH carries only the changed column in its
//   op data. The naive thing — reconstructing a FULL spaces row and
//   re-PUTing the create endpoint — would 400 (the create endpoint wants
//   floorId/roomTypeId, not a partial) and, because uploadData() acks the
//   batch only once EVERY entry succeeds, a thrown 400 wedges the ENTIRE
//   upload queue forever (every later mutation for every table blocks
//   behind this one entry). That is the exact failure
//   remintSpaceUnderNewId avoids by NOT re-pointing capture_items.space_id.
//   So this handler sends ONLY `{ alias }` to the dedicated alias
//   endpoint, which accepts a partial `{ alias }` body BY DESIGN — the
//   column-only PATCH maps to a VALID request, no wedge.
//
// Session id recovery: spaces rows carry building_id, not session_id, so
// we recover the owning session via loadSessionIdForBuilding(...,
// includeCompleted=true) (#667) — the same helper the spaces PUT + floors
// PUT use, honouring an in-batch session re-home. Passing includeCompleted
// lets a rename queued OFFLINE land on the building's COMPLETED session
// (the building was commonly completed by another actor during the
// surveyor's offline window); without it the queued edit was silently
// dropped. Only when NO session exists at all — open OR completed, e.g. a
// cancelled session (hard-deleted) — is the rename DROPPED (`superseded`)
// rather than thrown: a thrown entry would wedge the whole queue and block
// contractual count uploads behind a lost cosmetic rename.
//
// Error handling (mirrors the capture_items PATCH contract — none of these
// arms may throw, or the queue wedges):
//   - success (2xx) → emit `uploaded`.
//   - 404 not_found → the room was hard-deleted server-side (a concurrent
//     field delete), so the rename can NEVER land. Drop the op (ack) and
//     emit `dropped` (reason not_found) so a UI affordance can tell the
//     surveyor; the local row is already gone (or will be reconciled by
//     the spaces sync stream), so we do NOT issue a local DELETE here.
//     Do NOT throw.
//   - 409 / stale (failed_precondition) → another writer renamed the room
//     first (LWW, ADR-003). Reapply the user's intended alias ONCE (re-run
//     the local UPDATE, which queues a fresh spaces-PATCH that uploads next
//     cycle) and ack; on a SECOND consecutive conflict for the same room,
//     the user is losing to a faster concurrent editor, so drop the edit +
//     emit `dropped` (reason `superseded`) and let the spaces sync stream
//     deliver the winner's value. Caps at "retry exactly once" like the
//     capture_items path. `superseded` (not `not_found`) is used so the
//     DroppedChangeToast does NOT alarm the surveyor — a lost rename is
//     cosmetic, not contractual data-loss.
//   - any other 4xx/5xx → throw (entry stays queued, retried). A real
//     error must surface, not be silently dropped.

import type { CrudEntry } from '@powersync/web'
import type { PowerSyncWriteDb } from './powersync-db'
import * as Sentry from '@sentry/react'

import { ApiError, apiFetch, isNotFoundError } from '../api'

import type { DispatchContext } from './connector-dispatch-context'
import { loadSessionIdForBuilding } from './connector-dispatch-space'
import { _emitDropped, _emitUploaded } from './upload-events'

/**
 * Module-level retry counter keyed by spaces row id, tracking CONSECUTIVE
 * LWW conflicts for an alias edit. 0 → reapply the user's intent and ack
 * (counter → 1); 1+ → drop the edit + emit, reset. A successful upload
 * also resets. Mirrors `lwwAttempts` in connector.ts but scoped to the
 * alias path so the two counters can't interfere.
 *
 * Exported `_resetAliasLwwAttempts` so tests isolate cases without leaking
 * state across `it` blocks.
 */
const aliasLwwAttempts = new Map<string, number>()
export function _resetAliasLwwAttempts(): void {
  aliasLwwAttempts.clear()
}

/**
 * Read the `alias` value the optimistic local UPDATE wrote into the PATCH
 * op data. PowerSync only includes CHANGED columns, so `alias` is present
 * (it's the column that changed). `null` is a legitimate value (clear);
 * `undefined` would mean the op data somehow lacked alias — defensively
 * coerced to null so the request is still well-formed.
 */
function readAliasFromEntry(entry: CrudEntry): string | null {
  const data = entry.opData ?? {}
  const value = data['alias']
  if (typeof value === 'string') return value
  // SQLite NULL surfaces as null; anything else (incl. undefined) → null.
  return null
}

export async function dispatchSpaceUpdate(
  entry: CrudEntry,
  db: PowerSyncWriteDb,
  context: DispatchContext,
): Promise<void> {
  // building_id is NOT in a PATCH's opData — a PowerSync PATCH carries only
  // the CHANGED columns, and an alias edit changes only alias (+updated_at).
  // Read it from the local spaces ROW instead, which still exists (this is
  // an UPDATE, not a DELETE), to recover the owning session. Reading it from
  // opData would throw on EVERY real offline rename and wedge the queue
  // (Codex P1).
  const rows = await db.getAll<{ building_id: string | null }>(
    `SELECT building_id FROM spaces WHERE id = ? LIMIT 1`,
    [entry.id],
  )
  const buildingId = rows[0]?.building_id ?? null
  if (typeof buildingId !== 'string' || buildingId.length === 0) {
    // The local row is gone (deleted concurrently) or carries no
    // building_id — the rename can't be routed to a session. DROP it
    // (ack + emit `superseded`) rather than throw: a cosmetic rename must
    // never wedge the queue and block contractual count uploads.
    Sentry.captureException(
      new Error(
        'spaces alias edit dropped — local row missing / no building_id',
      ),
      {
        level: 'warning',
        extra: {
          reason: 'powersync_space_alias_patch_no_building_drop',
          entryId: entry.id,
        },
      },
    )
    console.warn(
      '[powersync] spaces alias PATCH — local row gone / no building_id; ' +
        'dropping the cosmetic rename to protect the upload queue',
      { id: entry.id },
    )
    _emitDropped(entry.table, entry.id, 'superseded')
    return
  }

  // includeCompleted=true (#667): a rename queued offline must land even if
  // the building's session was completed during the offline window — resolve
  // the building's completed session and upload, rather than dropping the
  // edit. An OPEN session still wins when one exists (the helper orders open
  // first); completed is only the fallback.
  const sessionId = await loadSessionIdForBuilding(db, buildingId, context, true)
  if (sessionId === null) {
    // No session AT ALL for the building — not even a completed one
    // (includeCompleted=true above already considers completed sessions).
    // The session was cancelled (cancelled sessions are hard-deleted) or has
    // not reached the local mirror, so the rename can't be routed to a
    // session server-side. A rename is COSMETIC, so DROP it (ack + emit
    // `superseded`) rather than throw: throwing wedges the ENTIRE upload
    // queue (uploadData acks only once every entry succeeds) and would block
    // the surveyor's CONTRACTUAL count uploads behind a lost cosmetic edit.
    // The spaces sync stream delivers the authoritative alias to the local
    // mirror. (Same queue-safety reasoning as the missing-row drop above.)
    Sentry.captureException(
      new Error('spaces alias edit dropped — building has no capture session'),
      {
        level: 'warning',
        extra: {
          reason: 'powersync_space_alias_patch_no_session_drop',
          entryId: entry.id,
          buildingId,
        },
      },
    )
    console.warn(
      '[powersync] spaces alias PATCH — building has no capture session ' +
        '(open or completed); dropping the cosmetic rename to protect the ' +
        'upload queue',
      { id: entry.id, buildingId },
    )
    _emitDropped(entry.table, entry.id, 'superseded')
    return
  }

  const alias = readAliasFromEntry(entry)
  await putAlias(entry, db, sessionId, alias)
}

async function putAlias(
  entry: CrudEntry,
  db: PowerSyncWriteDb,
  sessionId: string,
  alias: string | null,
): Promise<void> {
  try {
    // Send ONLY the alias — the dedicated alias endpoint accepts a partial
    // `{ alias }` body BY DESIGN. This is the wedge mitigation: a
    // column-only PATCH maps to a VALID request, so we never reconstruct a
    // full spaces row (which would 400 and wedge the queue).
    await apiFetch(
      `/capture/sessions/${encodeURIComponent(
        sessionId,
      )}/spaces/${encodeURIComponent(entry.id)}/alias`,
      { method: 'PUT', json: { alias } },
    )
    // Success — clear any prior conflict bookkeeping for this row, then
    // emit an upload event so subscribed UI / telemetry can flip the
    // "still uploading" affordance.
    aliasLwwAttempts.delete(entry.id)
    _emitUploaded(entry.table, entry.id)
    return
  } catch (err) {
    if (isNotFoundError(err)) {
      // The room was hard-deleted server-side (a concurrent field delete),
      // so the rename can NEVER land. Throwing would wedge the whole
      // upload queue (uploadData acks only once every entry succeeds), so
      // ack the op + emit `dropped` (reason not_found) for a UI affordance.
      // isNotFoundError covers a code-less 404 too (a proxy/CDN 404 with no
      // Encore code — H3/#694), so the surveyor's lost rename still emits
      // `dropped` here instead of vanishing through the backstop with no event.
      // The local spaces row is already gone (or the spaces sync stream
      // will reconcile it), so no local DELETE is issued here — and a
      // rename carries no contractual count, so a Sentry breadcrumb (not
      // an error) is enough.
      Sentry.captureException(err, {
        level: 'warning',
        extra: {
          reason: 'powersync_space_alias_patch_not_found_drop',
          entryId: entry.id,
          alias,
        },
      })
      console.warn(
        '[powersync] spaces alias PATCH not_found — room gone server-side, dropping the rename',
        { id: entry.id, alias },
      )
      _emitDropped(entry.table, entry.id, 'not_found')
      return
    }
    if (err instanceof ApiError && err.code === 'failed_precondition') {
      await handleAliasLwwConflict(entry, db, alias)
      return
    }
    // Any other 4xx/5xx is a real error or transient — throw so the entry
    // stays queued and retries. Never silently drop on an unknown failure.
    throw err
  }
}

/**
 * Reapply-once-then-drop on a stale/conflict response for an alias edit.
 * See the file header for the contract.
 *
 * 1st conflict for a row id: reapply the user's intended alias with a
 * fresh local `UPDATE spaces SET alias` — that generates a new spaces-PATCH
 * CRUD entry that retries next cycle. Increment the counter; ack the
 * failed entry so the SDK doesn't double-retry on top of our reapply.
 *
 * 2nd consecutive conflict: the user is losing to a faster concurrent
 * editor. Drop the edit (ack) + emit `dropped`; the spaces sync stream
 * delivers the winner's alias to the local mirror. Reset the counter.
 */
async function handleAliasLwwConflict(
  entry: CrudEntry,
  db: PowerSyncWriteDb,
  alias: string | null,
): Promise<void> {
  const attempts = aliasLwwAttempts.get(entry.id) ?? 0

  if (attempts === 0) {
    // First conflict — reapply the user's intent. The fresh local UPDATE
    // queues a new spaces-PATCH that will retry. (No syncVersion column on
    // spaces: the alias endpoint is a plain LWW set, so re-issuing the
    // same alias is all that's needed for the retry to converge.)
    await db.execute(
      `UPDATE spaces
         SET alias = ?,
             updated_at = strftime('%Y-%m-%dT%H:%M:%fZ','now')
       WHERE id = ?`,
      [alias, entry.id],
    )
    aliasLwwAttempts.set(entry.id, 1)
    return
  }

  // Second consecutive conflict — drop the edit and let the sync stream
  // deliver the winner. Emit `dropped` with reason `superseded` (NOT
  // not_found): a lost rename is cosmetic, so the DroppedChangeToast
  // suppresses it rather than telling the surveyor to re-capture.
  Sentry.captureException(
    new Error('spaces alias edit lost to concurrent rename (LWW)'),
    {
      level: 'warning',
      extra: {
        reason: 'powersync_space_alias_patch_lww_drop',
        entryId: entry.id,
        alias,
      },
    },
  )
  console.warn(
    '[powersync] spaces alias PATCH lost to a concurrent rename — dropping the edit',
    { id: entry.id, alias },
  )
  aliasLwwAttempts.delete(entry.id)
  _emitDropped(entry.table, entry.id, 'superseded')
}
